import React, { useState, useRef, useEffect } from 'react';
import Button from '../../components/Button/Button';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import Pagination from '../../components/Pagination/Pagination';
import { PetStatus, STATUS_LABELS, STATUS_COLORS, speciesEmoji } from '../../lib/utils';
import type { RescuePetPhotos } from '../../lib/rescueQueries';
import PetAvatar from '../../components/PetAvatar/PetAvatar';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import { trackPetChipMenuOpened, trackPetDeviceMenuOpened } from '../../analytics';
import styles from './Pets.module.scss';

interface DeviceMenuAction {
  label: string;
  key: string;
  dividerBefore?: boolean;
  danger?: boolean;
  fosterOnly?: boolean;
  rescueOnly?: boolean;
  hideWhenAdopted?: boolean;
}

const DEVICE_ACTIONS: DeviceMenuAction[] = [
  { label: 'View Device', key: 'view' },
  { label: 'Transfer to Adopter', key: 'transferAdopter', rescueOnly: true },
  { label: 'Transfer to Foster', key: 'transferFoster', rescueOnly: true },
  { label: 'Recall Device', key: 'recallDevice', fosterOnly: true },
  { label: 'Remove From Pet', key: 'removeFromPet', danger: true, dividerBefore: true, hideWhenAdopted: true },
];

function DeviceDropdown({
  device,
  onAction,
  petStatus,
}: {
  device: { id: string; moduleId: string };
  onAction: (key: string, moduleId: string) => void;
  petStatus?: PetStatus;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.deviceDropdown} ref={ref}>
      <button
        type="button"
        className={styles.deviceTag}
        onClick={() =>
          setOpen((prev) => {
            if (!prev) {
              trackPetDeviceMenuOpened();
            }
            return !prev;
          })
        }
      >
        <span className={styles.deviceTagIcon}>&#9673;</span>
        {device.moduleId}
        <span className={styles.deviceTagChevron}>&#9662;</span>
      </button>
      {open && (
        <ul className={styles.deviceMenu} role="menu">
          {DEVICE_ACTIONS.filter(
            (a) =>
              (!a.fosterOnly || petStatus === 'foster') &&
              (!a.rescueOnly || petStatus === 'rescue') &&
              (!a.hideWhenAdopted || petStatus !== 'adopted'),
          ).map((action) => (
            <React.Fragment key={action.key}>
              {action.dividerBefore && <li className={styles.deviceMenuDivider} role="separator" />}
              <li
                role="menuitem"
                tabIndex={0}
                className={`${styles.deviceMenuItem} ${action.danger ? styles.deviceMenuItemDanger : ''}`}
                onClick={() => {
                  onAction(action.key, device.moduleId);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onAction(action.key, device.moduleId);
                    setOpen(false);
                  }
                }}
              >
                {action.label}
              </li>
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}

interface ChipMenuAction {
  label: string;
  key: string;
  dividerBefore?: boolean;
  danger?: boolean;
  ownerOnly?: boolean;
  noOwnerOnly?: boolean;
  /**
   * Hide for pets in pending_adoption or adopted status. Both Add Owner and
   * Clear Owner call backend mutations (`assignNewOwnerToChip` / `recallChip`)
   * that overwrite `pet.householdFk` as a side effect — for pending_adoption
   * this would yank the pet out of its "Adoption: {petName}" household and
   * strand the transfer; for adopted it would yank the pet out of the
   * adopter's household. The Chips page applies the same gate via
   * `adoptedPetIds` for cross-page consistency.
   */
  hideOnAdoptionFlow?: boolean;
  /**
   * Needs a chip the Chips page can show. The roster's chip is matched by pet with no household
   * gating, so a pet microchipped by someone else carries one the rescue does not own; sending
   * them to a search that returns nothing is worse than not offering the action.
   */
  fiChipOnly?: boolean;
}

const CHIP_ACTIONS: ChipMenuAction[] = [
  { label: 'View Chip Details', key: 'viewChip', fiChipOnly: true },
  { label: 'Add Owner', key: 'addOwner', noOwnerOnly: true, hideOnAdoptionFlow: true },
  { label: 'Clear Owner', key: 'clearOwner', danger: true, ownerOnly: true, hideOnAdoptionFlow: true },
];

function ChipDropdown({
  chip,
  hasOwner,
  petStatus,
  onAction,
}: {
  chip: { id: string; serialNumber: string; isFiChip?: boolean };
  hasOwner: boolean;
  petStatus?: PetStatus;
  onAction: (key: string, chipId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const actions = CHIP_ACTIONS.filter(
    (a) =>
      (!a.ownerOnly || hasOwner) &&
      (!a.noOwnerOnly || !hasOwner) &&
      (!a.hideOnAdoptionFlow || (petStatus !== 'pending_adoption' && petStatus !== 'adopted')) &&
      (!a.fiChipOnly || chip.isFiChip !== false),
  );

  // An adopted-out pet carrying someone else's microchip filters every action out: the two
  // owner ones by the adoption flow, View Chip Details by the chip not being findable. Render
  // the tag as plain text rather than a control that opens an empty menu — the serial is still
  // worth showing, the affordance is not.
  if (actions.length === 0) {
    return (
      <span className={styles.chipTagBtn}>
        <span className={styles.chipTagIcon}>&#9672;</span>
        {chip.serialNumber}
      </span>
    );
  }

  return (
    <div className={styles.deviceDropdown} ref={ref}>
      <button
        type="button"
        className={styles.chipTagBtn}
        onClick={() =>
          setOpen((prev) => {
            if (!prev) {
              trackPetChipMenuOpened();
            }
            return !prev;
          })
        }
      >
        <span className={styles.chipTagIcon}>&#9672;</span>
        {chip.serialNumber}
        <span className={styles.deviceTagChevron}>&#9662;</span>
      </button>
      {open && (
        <ul className={styles.deviceMenu} role="menu">
          {actions.map((action) => (
            <React.Fragment key={action.key}>
              {action.dividerBefore && <li className={styles.deviceMenuDivider} role="separator" />}
              <li
                role="menuitem"
                tabIndex={0}
                className={`${styles.deviceMenuItem} ${action.danger ? styles.deviceMenuItemDanger : ''}`}
                onClick={() => {
                  onAction(action.key, chip.id);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onAction(action.key, chip.id);
                    setOpen(false);
                  }
                }}
              >
                {action.label}
              </li>
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChipCell({
  chip,
  petId,
  petStatus,
  onChipAction,
}: {
  chip: {
    id: string;
    shortId: string;
    serialNumber: string;
    /**
     * Forwarded to `ChipDropdown`, which gates View Chip Details on it. Named here rather than
     * relying on the object arriving intact: reconstructing this prop without the flag would
     * turn the gate off silently, with no type error to catch it.
     */
    isFiChip?: boolean;
    latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  };
  petId: string;
  petStatus?: PetStatus;
  onChipAction?: (actionKey: string, chipId: string, shortId: string, serialNumber: string, petId: string) => void;
}) {
  return (
    <ChipDropdown
      chip={chip}
      hasOwner={!!chip.latestContactHistoryEntry?.contactInfo?.primaryEmail}
      petStatus={petStatus}
      onAction={(key, chipId) => onChipAction?.(key, chipId, chip.shortId, chip.serialNumber, petId)}
    />
  );
}

function timeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) {
    return 'just now';
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }
  const weeks = Math.floor(days / 7);
  if (weeks < 5) {
    return `${weeks}w ago`;
  }
  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months}mo ago`;
  }
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

export const PAGE_SIZE_OPTIONS = [10, 25, 50];

export interface PetRow {
  id: string;
  name: string;
  species?: string | null;
  photos?: RescuePetPhotos | null;
  updatedAt?: string | null;
  breed?: { name: string } | null;
  device?: { id: string; moduleId: string } | null;
  chip?: {
    id: string;
    shortId: string;
    serialNumber: string;
    /** False for a microchip the rescue does not own — View Chip Details cannot find it. */
    isFiChip?: boolean;
    latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  } | null;
  hasDevice: boolean;
  hasChip: boolean;
  status?: PetStatus;
}

/** Columns the table can be ordered by; the server does the ordering. */
export type PetsSortKey = 'name' | 'chip' | 'device' | 'status';

interface PetsTableProps {
  /** The current page, already ordered, filtered and sliced by the server. */
  pets: PetRow[];
  /** Every pet matching the search and filter — what the heading counts. */
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** The roster query failed. Shown in place of the rows only when there are none to lose. */
  hasError: boolean;
  onRetry: () => void;
  /** Showing the last page while the next is in flight — dim what is there, don't replace it. */
  transitioning?: boolean;
  sortKey: PetsSortKey | null;
  sortDir: 'asc' | 'desc';
  onSort: (key: PetsSortKey) => void;
  loading: boolean;
  onAssignDevice?: (petId: string) => void;
  onViewPet?: (pet: PetRow) => void;
  onCreatePet?: () => void;
  onDeviceAction?: (actionKey: string, moduleId: string, petId: string) => void;
  onChipAction?: (actionKey: string, chipId: string, shortId: string, serialNumber: string, petId: string) => void;
  onAddChip?: (pet: PetRow) => void;
}

export default function PetsTable({
  pets,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  hasError,
  onRetry,
  transitioning = false,
  sortKey,
  sortDir,
  onSort,
  loading,
  onAssignDevice,
  onViewPet,
  onCreatePet,
  onDeviceAction,
  onChipAction,
  onAddChip,
}: PetsTableProps) {
  function handlePageSizeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    onPageSizeChange(Number(e.target.value));
  }

  // The failure speaks only when there is nothing else to say. A refetch can fail with the last
  // page still in hand — and every mutation on this page refetches — so taking the rows down on
  // `hasError` alone would drop the user out of a table they were working in over one bad
  // response. The count is suppressed with them: a roster that failed to load is not a roster
  // of zero pets, which is exactly what "Your Pets (0)" claims.
  const showError = hasError && pets.length === 0;
  const showCount = !loading && !showError;

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 className={styles.sectionTitle}>Your Pets{showCount ? ` (${totalCount})` : ''}</h2>
        </div>
        {onCreatePet && (
          <Button variant="green" size="sm" onClick={onCreatePet}>
            +Add Pet
          </Button>
        )}
      </div>

      <div className={`${styles.tableWrapper}${transitioning ? ` ${styles.tableTransitioning}` : ''}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.sortableHeader} onClick={() => onSort('name')}>
                Pet {sortKey === 'name' && <span className={styles.sortArrow}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              <th className={styles.sortableHeader} onClick={() => onSort('status')}>
                Status{' '}
                {sortKey === 'status' && <span className={styles.sortArrow}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              <th className={styles.sortableHeader} onClick={() => onSort('chip')}>
                Chip {sortKey === 'chip' && <span className={styles.sortArrow}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              <th className={styles.sortableHeader} onClick={() => onSort('device')}>
                Device{' '}
                {sortKey === 'device' && <span className={styles.sortArrow}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              <th style={{ textAlign: 'right' }} />
            </tr>
          </thead>
          <tbody>
            {/* 61px = the measured height of a real pet row: 24px td padding + the
                36px avatar that sets the name cell's height + 1px bottom border. */}
            {showError && (
              <tr>
                <td colSpan={5} className={styles.errorRow} role="alert">
                  <span className={styles.errorRowContent}>
                    <span>Could not load your pets.</span>
                    <Button variant="ghost" size="sm" onClick={onRetry}>
                      Retry
                    </Button>
                  </span>
                </td>
              </tr>
            )}
            {!showError && loading && <SkeletonRows count={pageSize} columns={5} rowHeight={61} />}
            {!showError && !loading && pets.length === 0 && (
              <tr>
                <td colSpan={5} className={styles.emptyRow}>
                  No pets found
                </td>
              </tr>
            )}
            {!showError &&
              !loading &&
              pets.map((pet) => (
                <tr key={pet.id}>
                  <td>
                    <div className={styles.petNameCell}>
                      <PetAvatar
                        photoThumbUrl={pet.photos?.first?.image?.thumb ?? null}
                        fallback={speciesEmoji(pet.species)}
                        className={styles.petAvatar}
                        imgClassName={styles.petAvatarImg}
                      />
                      <div>
                        <div style={{ fontWeight: 700 }}>{pet.name}</div>
                        <div className={styles.petBreed}>{pet.breed?.name ?? '\u2014'}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={styles.statusCell}>
                      <span className={styles[`statusBadge_${STATUS_COLORS[pet.status ?? 'rescue']}`]}>
                        {STATUS_LABELS[pet.status ?? 'rescue']}
                      </span>
                      {pet.updatedAt && (
                        <>
                          <span className={styles.statusDot}>&middot;</span>
                          <span className={styles.statusDate}>{timeAgo(pet.updatedAt)}</span>
                        </>
                      )}
                    </span>
                  </td>
                  <td>
                    {pet.hasChip && pet.chip && (
                      <ChipCell chip={pet.chip} petId={pet.id} petStatus={pet.status} onChipAction={onChipAction} />
                    )}
                    {!pet.hasChip && pet.status === 'adopted' && <span className={styles.emptyCell}>&mdash;</span>}
                    {!pet.hasChip && onAddChip && pet.status !== 'adopted' && (
                      <button type="button" className={styles.addDeviceBtn} onClick={() => onAddChip(pet)}>
                        +Add
                      </button>
                    )}
                  </td>
                  <td>
                    <div className={styles.setupPills}>
                      {pet.hasDevice && pet.device && (
                        <DeviceDropdown
                          device={pet.device}
                          onAction={(key, moduleId) => onDeviceAction?.(key, moduleId, pet.id)}
                          petStatus={pet.status}
                        />
                      )}
                      {!pet.hasDevice && pet.status === 'adopted' && <span className={styles.emptyCell}>&mdash;</span>}
                      {!pet.hasDevice && onAssignDevice && pet.status !== 'adopted' && (
                        <button type="button" className={styles.addDeviceBtn} onClick={() => onAssignDevice(pet.id)}>
                          +Add
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className={styles.rowActions} style={{ justifyContent: 'flex-end' }}>
                      {onViewPet && (
                        <button type="button" className={styles.viewBtn} onClick={() => onViewPet(pet)}>
                          View
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className={styles.paginationWrapper} style={loading ? { visibility: 'hidden' } : undefined}>
        <div className={styles.pageSizeRow}>
          <label className={styles.pageSizeLabel}>
            Rows per page
            <PortalSelect value={pageSize} onChange={handlePageSizeChange} className={styles.pageSizeSelect}>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </PortalSelect>
          </label>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalCount}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
